/**
 * Operator attestation / upstream-input for operational recovery gates.
 * Lab/simulator proof never becomes “production closed” via Approve alone —
 * operators provide transfer logs / evidence, or decline → conclude unresolved.
 */
import { useCallback, useSyncExternalStore } from 'react';

export type OpsAttestationDecision =
  | 'upstream_evidence'
  | 'decline_upstream'
  /** @deprecated Prefer upstream_evidence; ignored for lab paths in assessment. */
  | 'approve_production'
  /** @deprecated Alias of decline_upstream. */
  | 'reject_lab';

export interface OpsHumanAttestation {
  decision: OpsAttestationDecision;
  at: string;
  by: string;
  note?: string;
}

const EVENT = 'ensomirai:ops-attestation';
const RERUN_EVENT = 'ensomirai:ops-rerun';

const key = (projectId: string, incidentId: string) =>
  `ensomirai.ops.attestation.v1.${projectId}.${incidentId}`;

const rerunKey = (projectId: string, incidentId: string) =>
  `ensomirai.ops.rerun.v1.${projectId}.${incidentId}`;

const VALID: ReadonlySet<string> = new Set([
  'upstream_evidence',
  'decline_upstream',
  'approve_production',
  'reject_lab',
]);

/** Stable snapshots for useSyncExternalStore (Object.is) — never return a fresh object each read. */
const attestationSnapshotCache = new Map<
  string,
  { raw: string | null; value: OpsHumanAttestation | null }
>();
const rerunSnapshotCache = new Map<string, { raw: string | null; value: boolean }>();

function notify(projectId: string, incidentId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT, { detail: { projectId, incidentId } }),
    );
  } catch {
    /* ignore */
  }
}

function notifyRerun(projectId: string, incidentId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(RERUN_EVENT, { detail: { projectId, incidentId } }),
    );
  } catch {
    /* ignore */
  }
}

function readAttestationSnapshot(projectId: string, incidentId: string): OpsHumanAttestation | null {
  if (!projectId || !incidentId) return null;
  const k = key(projectId, incidentId);
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(k);
  } catch {
    raw = null;
  }
  const cached = attestationSnapshotCache.get(k);
  if (cached && cached.raw === raw) return cached.value;

  let value: OpsHumanAttestation | null = null;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as OpsHumanAttestation;
      if (parsed?.decision && VALID.has(parsed.decision)) value = parsed;
    } catch {
      value = null;
    }
  }
  attestationSnapshotCache.set(k, { raw, value });
  return value;
}

function readRerunSnapshot(projectId: string, incidentId: string): boolean {
  if (!projectId || !incidentId) return false;
  const k = rerunKey(projectId, incidentId);
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(k);
  } catch {
    raw = null;
  }
  // Auto-expire stuck rerun grace (HMR / crashed mid-analyze left the flag on).
  if (raw) {
    const age = Date.now() - Date.parse(raw);
    if (!Number.isFinite(age) || age > 90_000) {
      try {
        sessionStorage.removeItem(k);
      } catch {
        /* ignore */
      }
      rerunSnapshotCache.set(k, { raw: null, value: false });
      return false;
    }
  }
  const cached = rerunSnapshotCache.get(k);
  if (cached && cached.raw === raw) return cached.value;
  const value = Boolean(raw);
  rerunSnapshotCache.set(k, { raw, value });
  return value;
}

export function loadOpsHumanAttestation(
  projectId: string,
  incidentId: string,
): OpsHumanAttestation | null {
  return readAttestationSnapshot(projectId, incidentId);
}

export function saveOpsHumanAttestation(
  projectId: string,
  incidentId: string,
  decision: OpsAttestationDecision,
  opts?: { by?: string; note?: string },
): OpsHumanAttestation {
  const record: OpsHumanAttestation = {
    decision,
    at: new Date().toISOString(),
    by: opts?.by || 'operator',
    note: opts?.note,
  };
  const k = key(projectId, incidentId);
  const raw = JSON.stringify(record);
  try {
    localStorage.setItem(k, raw);
  } catch {
    /* ignore quota */
  }
  attestationSnapshotCache.set(k, { raw, value: record });
  notify(projectId, incidentId);
  return record;
}

export function clearOpsHumanAttestation(projectId: string, incidentId: string): void {
  const k = key(projectId, incidentId);
  try {
    localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
  attestationSnapshotCache.set(k, { raw: null, value: null });
  notify(projectId, incidentId);
}

/** Mark this incident as operator-rerun so the UI re-opens the recovery flow. */
export function markOpsMissionRerun(projectId: string, incidentId: string): void {
  if (!projectId || !incidentId) return;
  const k = rerunKey(projectId, incidentId);
  const raw = new Date().toISOString();
  try {
    sessionStorage.setItem(k, raw);
  } catch {
    /* ignore */
  }
  rerunSnapshotCache.set(k, { raw, value: true });
  clearOpsHumanAttestation(projectId, incidentId);
  notifyRerun(projectId, incidentId);
}

export function clearOpsMissionRerun(projectId: string, incidentId: string): void {
  const k = rerunKey(projectId, incidentId);
  try {
    sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
  rerunSnapshotCache.set(k, { raw: null, value: false });
  notifyRerun(projectId, incidentId);
}

/** Age of the active rerun grace flag in ms, or null if inactive. */
export function opsMissionRerunAgeMs(projectId: string, incidentId: string): number | null {
  if (!projectId || !incidentId) return null;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(rerunKey(projectId, incidentId));
  } catch {
    raw = null;
  }
  if (!raw) return null;
  const age = Date.now() - Date.parse(raw);
  return Number.isFinite(age) ? age : null;
}

export function isOpsMissionRerunActive(projectId: string, incidentId: string): boolean {
  return readRerunSnapshot(projectId, incidentId);
}

/** Full attestation record (includes note / evidence text). */
export function useOpsHumanAttestationRecord(
  projectId: string,
  incidentId: string | null | undefined,
): OpsHumanAttestation | null {
  const id = incidentId || '';
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent<{ projectId?: string; incidentId?: string }>).detail;
        if (detail && detail.projectId === projectId && detail.incidentId === id) {
          onStoreChange();
        }
      };
      const onStorage = (e: StorageEvent) => {
        if (!e.key || e.key === key(projectId, id)) onStoreChange();
      };
      window.addEventListener(EVENT, handler);
      window.addEventListener('storage', onStorage);
      return () => {
        window.removeEventListener(EVENT, handler);
        window.removeEventListener('storage', onStorage);
      };
    },
    [projectId, id],
  );
  const getSnapshot = useCallback(
    () => readAttestationSnapshot(projectId, id),
    [projectId, id],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

/** Subscribe so Mission header / Executions update when attestation changes. */
export function useOpsHumanAttestation(
  projectId: string,
  incidentId: string | null | undefined,
): OpsAttestationDecision | null {
  return useOpsHumanAttestationRecord(projectId, incidentId)?.decision ?? null;
}

export function useOpsMissionRerunActive(
  projectId: string,
  incidentId: string | null | undefined,
): boolean {
  const id = incidentId || '';
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent<{ projectId?: string; incidentId?: string }>).detail;
        if (detail && detail.projectId === projectId && detail.incidentId === id) {
          onStoreChange();
        }
      };
      window.addEventListener(RERUN_EVENT, handler);
      window.addEventListener(EVENT, handler);
      return () => {
        window.removeEventListener(RERUN_EVENT, handler);
        window.removeEventListener(EVENT, handler);
      };
    },
    [projectId, id],
  );
  const getSnapshot = useCallback(
    () => readRerunSnapshot(projectId, id),
    [projectId, id],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
